import * as React from 'react';
import { Grid, GridColumn } from '@progress/kendo-react-grid';
import { accentUtils, t, showDialog } from '../services/HelperService';
import { queryNoTracking, update } from '../services/DataService';
import { AccentComboUnbound } from '../controls/unbound/AccentComboUnbound';
import { AccentTextAreaUnbound } from '../controls/unbound/AccentTextAreaUnbound';
import { AccentCheckUnbound } from '../controls/unbound/AccentCheckUnbound';
import { AccentSpinner } from '../controls/AccentSpinner';
import { AccentDialog } from './AccentDialog';
import { AccentButton } from '../controls/AccentButton';

const ACTIVITY_TYPES_OPPORTUNITY = ["Sales Appointment", "Showroom Quote"];

const query_PL_ActivityCancelReasons = queryNoTracking("PL_ActivityCancelReasons");
const query_PL_QuoteOutcomeReasonsForOpportunity = queryNoTracking("PL_QuoteOutcomeReasonsForOpportunity");
const query_Opportunity_GetForActivityCancel = queryNoTracking("Opportunity_GetForActivityCancel");

export const showActivityCancel = (activity, options = {}) => {
    const isMultipleCancel = options.skipSave && options.activityIndex != null && options.totalActivities != null && options.totalActivities > 1;

    return showDialog(<ActivityCancelDlg
        activity={activity}
        isMultipleCancel={isMultipleCancel}
        activityIndex={options.activityIndex}
        totalActivities={options.totalActivities}
    />).then(result => {

        if (result?.canceled) return null;
        if (options.skipSave) return { activity, result };

        return update("Calendar_CancelActivity", {
            ID: activity.ID,
            PL_ActivityCancelReasonID: result.PL_ActivityCancelReasonID,
            CancelledNotes: result.CancelledNotes || null,
            UpdateOpportunity: result.UpdateOpportunity === true,
            OpportunityLost: result.OpportunityLost ?? undefined,
            OpportunityID: result.OpportunityID ?? undefined,
            PL_QuoteOutcomeReasonID: result.PL_QuoteOutcomeReasonID ?? undefined

        }).then(() => result);
    });
};

const getOpportunityStatusText = (result, t) => {
    if (result.OpportunityLost) return t("application_strings.views.activitiesView.cancelSummaryYes");
    if (result.OpportunityID) return t("application_strings.views.activitiesView.cancelSummaryMoveToLead");
    return "-";
};

const getCancelReasonText = (result) => {
    const reason = result.CancelReasonDescription || "-";
    const outcome = result.OutcomeReasonDescription ? ` (${result.OutcomeReasonDescription})` : "";
    return `${reason}${outcome}`;
};

const CANCEL_ACTIVITY_CONFIRM_KEY = "application_strings.views.activitiesView.cancelActivityConfirm";

export const showActivityCancelSummary = (pending, options = {}) => {
    const { refresh } = options;

    showDialog(({ onClose }) =>
        <ActivityCancelSummaryDlg pending={pending} onClose={onClose} confirmKey={CANCEL_ACTIVITY_CONFIRM_KEY} />
    ).then(res => {

        if (res?.canceled || res !== CANCEL_ACTIVITY_CONFIRM_KEY) return;

        const cancelPayloads = pending.map(({ activity, result }) => ({
            ID: activity.ID,
            PL_ActivityCancelReasonID: result.PL_ActivityCancelReasonID,
            CancelledNotes: result.CancelledNotes || null,
            UpdateOpportunity: result.UpdateOpportunity === true,
            OpportunityLost: result.OpportunityLost ?? undefined,
            OpportunityID: result.OpportunityID ?? undefined,
            PL_QuoteOutcomeReasonID: result.PL_QuoteOutcomeReasonID ?? undefined
        }));

        return Promise.all(cancelPayloads.map(payload => update("Calendar_CancelActivity", payload)))
            .then(() => refresh?.());
    });
};

const ActivityCancelSummaryDlg = React.memo(({ pending, onClose, confirmKey }) => {

    const gridData = React.useMemo(() => pending.map((p, i) => ({
        id: p.activity.ID || i,
        Subject: p.activity.Subject || "-",
        Opportunity: getOpportunityStatusText(p.result, t),
        Reason: getCancelReasonText(p.result),
        Notes: p.result.CancelledNotes || "-"
    })), [pending]);

    return (
        <AccentDialog
            headerContent={t("application_strings.views.activitiesView.cancelActivitySummaryTitle")}
            maxWidth={800}
            onClose={onClose}
            cancelLabel={"application_strings.application.dialogs.activityCancelDlg.keepActivityButton"}
            actionsContent={[
                <AccentButton key="confirm" onClick={() => onClose(confirmKey)}>
                    {t(confirmKey)}
                </AccentButton>
            ]}
        >
            <Grid data={gridData} dataItemKey="id" style={{ height: "400px", maxWidth: "800px" }}>
                <GridColumn field="Subject" title={t("application_strings.entities.activity.Subject")} />
                <GridColumn field="Opportunity" title={t("application_strings.views.activitiesView.cancelSummaryOpportunityLost")} />
                <GridColumn field="Reason" title={t("application_strings.views.activitiesView.cancelSummaryReason")} />
                <GridColumn field="Notes" title={t("application_strings.views.activitiesView.cancelSummaryNotes")} />
            </Grid>
        </AccentDialog>
    );
});

export const ActivityCancelDlg = React.memo(({ onClose, activity, isMultipleCancel, activityIndex, totalActivities }) => {
    const [cancelReasonId, setCancelReasonId] = React.useState(null);
    const [cancelledNotes, setCancelledNotes] = React.useState("");
    const [reasons, setReasons] = React.useState(null);
    const [isOpportunityLost, setIsOpportunityLost] = React.useState(false);
    const [outcomeReasonId, setOutcomeReasonId] = React.useState(null);
    const [outcomeReasons, setOutcomeReasons] = React.useState(null);
    const [opportunityIsAlreadyLost, setOpportunityIsAlreadyLost] = React.useState(null);
    const [hasOtherSalesAppointments, setHasOtherSalesAppointments] = React.useState(false);
    const [hasJobs, setHasJobs] = React.useState(false);

    const hasOpportunity = activity && ACTIVITY_TYPES_OPPORTUNITY.includes(activity.ActivityType) && activity.OpportunityID;


    React.useEffect(() => {
        query_PL_ActivityCancelReasons.getAllWithCache({ includeID: null, activeOnly: true, activityType: activity?.ActivityType })
            .then(r => setReasons(r));
    }, [activity?.ActivityType]);


    React.useEffect(() => {
        if (hasOpportunity && activity?.OpportunityID) {

            query_Opportunity_GetForActivityCancel.getFirstOrDefault({ id: activity.OpportunityID, excludeActivityID: activity.ID })
                .then(r => {
                    const isLost = r?.IsLost ?? false;
                    setOpportunityIsAlreadyLost(isLost);
                    setHasOtherSalesAppointments(r?.HasOtherSalesAppointments ?? false);
                    setHasJobs(r?.HasJobs ?? false);
                    if (isLost) setIsOpportunityLost(true);
                })
                .catch(() => {
                    setOpportunityIsAlreadyLost(false);
                    setHasOtherSalesAppointments(false);
                    setHasJobs(false);
                });

        } else {
            setOpportunityIsAlreadyLost(null);
            setHasOtherSalesAppointments(false);
            setHasJobs(false);
        }
    }, [hasOpportunity, activity?.OpportunityID, activity?.ID]);

    React.useEffect(() => {
        if (isOpportunityLost && hasOpportunity && activity?.OpportunityID) {
            query_PL_QuoteOutcomeReasonsForOpportunity.getAllWithCache({ id: activity.OpportunityID, activeOnly: true })
                .then(r => setOutcomeReasons(r));
        } else {
            setOutcomeReasons(null);
            setOutcomeReasonId(null);
        }
    }, [isOpportunityLost, hasOpportunity, activity?.OpportunityID]);

    const outcomeReasonRequired = hasOpportunity && isOpportunityLost && opportunityIsAlreadyLost !== true;
    const outcomeReasonValid = !outcomeReasonRequired || !accentUtils.isEmpty(outcomeReasonId);
    const allowConfirm = !accentUtils.isEmpty(cancelReasonId) && outcomeReasonValid;

    const onConfirm = () => {
        if (accentUtils.isEmpty(cancelReasonId)) return;
        if (outcomeReasonRequired && accentUtils.isEmpty(outcomeReasonId)) return;
        const cancelReason = reasons?.find(r => r.ID === cancelReasonId);
        const outcomeReason = outcomeReasons?.find(r => r.ID === outcomeReasonId);
        const result = {
            canceled: false,
            PL_ActivityCancelReasonID: cancelReasonId,
            CancelledNotes: cancelledNotes || null,
            CancelReasonDescription: cancelReason?.Description ?? null,
            OutcomeReasonDescription: outcomeReason?.Description ?? null
        };
        if (hasOpportunity && opportunityIsAlreadyLost !== true) {
            result.OpportunityID = activity.OpportunityID;
            if (isOpportunityLost) {
                result.UpdateOpportunity = true;
                result.OpportunityLost = true;
                result.PL_QuoteOutcomeReasonID = outcomeReasonId;
            } else if (!hasOtherSalesAppointments && !hasJobs) {
                result.UpdateOpportunity = true;
            }
        }
        onClose(result);
    };


    const baseTitle = t("application_strings.application.dialogs.activityCancelDlg.title");



    const indexSuffix = isMultipleCancel && activityIndex != null && totalActivities != null
        ? ` (${t("application_strings.application.dialogs.activityCancelDlg.indexOfTotal", { index: activityIndex, total: totalActivities })})`
        : "";

    const headerTitle = accentUtils.isEmpty(activity?.Subject)
        ? `${baseTitle}${indexSuffix}`
        : `${baseTitle}${indexSuffix} - ${activity.Subject}`;


    if (accentUtils.isNull(reasons)) {
        return <AccentDialog headerContent={headerTitle} onClose={onClose}><AccentSpinner /></AccentDialog>;
    }

    return (
        <AccentDialog
            headerContent={headerTitle}
            onClose={onClose}
            maxWidth={600}
            cancelLabel={isMultipleCancel
                ? "application_strings.application.dialogs.activityCancelDlg.skipActivityButton"
                : "application_strings.application.dialogs.activityCancelDlg.keepActivityButton"}
            actionsContent={[
                <AccentButton key="confirm" disabled={!allowConfirm} onClick={onConfirm}>
                    {t(isMultipleCancel ? "application_strings.application.dialogs.activityCancelDlg.nextButton" : "application_strings.application.dialogs.activityCancelDlg.confirmButton")}
                </AccentButton>
            ]}
        >
            <div className="d-flex flex-column gap-2">
                <AccentComboUnbound
                    displayField="Description"
                    valueField="ID"
                    onChange={setCancelReasonId}
                    label="application_strings.application.dialogs.activityCancelDlg.reasonLabel"
                    data={reasons}
                    required
                />
                {hasOpportunity && (
                    <>
                        <AccentCheckUnbound
                            key={opportunityIsAlreadyLost === true ? "lost" : "open"}
                            label="application_strings.application.dialogs.activityCancelDlg.opportunityLostPrompt"
                            defaultValue={opportunityIsAlreadyLost === true}
                            disabled={opportunityIsAlreadyLost === true}
                            onChange={e => setIsOpportunityLost(e.checked)}
                        />
                        {isOpportunityLost && opportunityIsAlreadyLost !== true && (
                            accentUtils.isNull(outcomeReasons) ? (
                                <AccentSpinner />
                            ) : (
                                <AccentComboUnbound
                                    displayField="Description"
                                    valueField="ID"
                                    onChange={setOutcomeReasonId}
                                    label="application_strings.application.dialogs.activityCancelDlg.outcomeReasonLabel"
                                    data={outcomeReasons}
                                    required
                                />
                            )
                        )}
                    </>
                )}
                <AccentTextAreaUnbound
                    label="application_strings.application.dialogs.activityCancelDlg.notesLabel"
                    defaultValue={cancelledNotes}
                    onChange={setCancelledNotes}
                    rows={3}
                />
            </div>
        </AccentDialog>
    );
});
